<!-- HotX Console 0.1.121 域名续费状态单一数据源。实际修改时间：2026-09-29 15:24:00 CST；精确父基线：15d702125b8f53ed4d79eca5caa49513ed8faf42；变更：CONSOLE-HOME-DATA-PASSWORD-20260929。 -->
<script setup lang="ts">
import { computed, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { DomainPlanOverview } from '../api/pointApi'
import { useDomainDailyRenewState } from './domainDailyRenewState'

defineOptions({ name: 'DomainDailyRenewCell' })

const props = withDefaults(defineProps<{
  domainId: number
  domainLabel?: string
  overview: DomainPlanOverview | null
  loading?: boolean
  loadError?: string
}>(), {
  loading: false,
  loadError: ''
})
const emit = defineEmits<{
  overviewUpdated: [overview: DomainPlanOverview]
}>()

const state = useDomainDailyRenewState()
const isConfirming = ref(false)
const effectiveOverview = computed(() => props.overview)
const plan = computed(() => effectiveOverview.value?.domains.find(item => item.domainId === props.domainId) || null)
const pointPrice = computed(() => effectiveOverview.value?.dailyPointPrice ?? 1)
const isUpdating = computed(() => state.updatingDomainIds.value.has(props.domainId))
const isAnyUpdating = computed(() => state.updatingDomainIds.value.size > 0)
const disabledReason = computed(() => plan.value?.switchBlockedMessage || '')
const displayLoading = computed(() => Boolean(props.loading))
const displayError = computed(() => props.loadError || '')

async function changeDailyAutoRenew(value: string | number | boolean) {
  if (isConfirming.value || isAnyUpdating.value || displayLoading.value) return
  const enabled = Boolean(value)

  // V1.3 防误触门禁：开关本身不直接提交，用户明确确认后才调用原有保存接口。
  // 关闭只影响未来续费；开启文案同步说明后端“一域名一自然日一扣”的幂等规则。
  isConfirming.value = true
  try {
    await ElMessageBox.confirm(
      enabled
        ? `开启后，系统会在需要续费且今日尚未扣费时扣除 ${pointPrice.value} 积分并续期 1 天；同一自然日最多只会扣除一次。`
        : '关闭后只停止未来自动扣费，已扣积分不会退回，已付费有效期会完整保留。',
      enabled ? '确认开启自动续费？' : '确认关闭自动续费？',
      {
        confirmButtonText: enabled ? '确认开启' : '确认关闭',
        cancelButtonText: enabled ? '暂不开启' : '暂不关闭',
        type: enabled ? 'warning' : 'info',
        closeOnClickModal: false,
        distinguishCancelAndClose: true
      }
    )
  } catch {
    isConfirming.value = false
    return
  }

  try {
    const result = await state.setDailyAutoRenew(props.domainId, enabled)
    emit('overviewUpdated', result)
    if (result.dailyChargeApplied) {
      window.dispatchEvent(new CustomEvent('hotx:points-updated'))
    }
    ElMessage.success(enabled
      ? result.dailyChargeApplied
        ? '已扣除 1 积分并续期 1 天；今日不会再次扣费。'
        : '已开启自动续费；今日已扣费或仍在有效期内，不会重复扣积分。'
      : '已关闭自动续费；当前已付费有效期保留，之后不再自动扣费。')
  } catch (error) {
    ElMessage.error(state.concreteError(error, '无法判断自动续费设置失败的具体原因。'))
  } finally {
    isConfirming.value = false
  }
}
</script>

<template>
  <span
    v-if="displayError"
    class="daily-renew-error"
    :title="displayError"
  >{{ displayError }}</span>

  <div
    v-else-if="plan?.billingMode === 'FREE'"
    class="daily-renew-card"
    :class="{ 'daily-renew-card--enabled': plan.dailyAutoRenew }"
    :title="disabledReason"
    data-test="domain-daily-renew-card"
  >
    <div class="daily-renew-card__control">
      <el-switch
        :model-value="plan.dailyAutoRenew"
        :loading="isUpdating || displayLoading"
        :disabled="plan.switchBlocked || isAnyUpdating || isConfirming || displayLoading"
        :aria-label="`${domainLabel || plan.domain} 自动续费`"
        data-test="domain-daily-renew-switch"
        @change="changeDailyAutoRenew"
      />
      <span class="daily-renew-card__label">自动续费</span>
    </div>
    <div class="daily-renew-card__price">
      <span class="daily-renew-card__coin" aria-hidden="true">1</span>
      <span>每天只需要{{ pointPrice }}积分</span>
    </div>
  </div>

  <div v-else-if="displayLoading && !effectiveOverview" class="daily-renew-placeholder" aria-label="正在加载自动续费状态">
    <span class="daily-renew-placeholder__line" />
    <span class="daily-renew-placeholder__line daily-renew-placeholder__line--short" />
  </div>

  <span v-else class="daily-renew-unavailable">不适用</span>
</template>

<style scoped>
.daily-renew-card {
  box-sizing: border-box;
  width: 100%;
  min-width: 176px;
  max-width: 206px;
  padding: 9px 11px 8px;
  overflow: hidden;
  color: #214936;
  background:
    radial-gradient(circle at 88% 12%, rgb(225 191 89 / 20%), transparent 34%),
    linear-gradient(145deg, #f8fbf4 0%, #edf6e9 100%);
  border: 1px solid #d4e2ce;
  border-radius: 12px;
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 82%),
    0 5px 14px rgb(25 82 54 / 7%);
  animation: daily-renew-arrive 360ms cubic-bezier(.2, .8, .2, 1) both;
}

.daily-renew-card--enabled {
  border-color: #bfd9c4;
  box-shadow:
    inset 0 1px 0 rgb(255 255 255 / 88%),
    0 6px 16px rgb(25 105 67 / 11%);
}

.daily-renew-card__control,
.daily-renew-card__price {
  display: flex;
  align-items: center;
}

.daily-renew-card__control {
  gap: 8px;
}

.daily-renew-card__label {
  overflow: hidden;
  font-size: 13px;
  font-weight: 700;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.daily-renew-card__price {
  gap: 6px;
  margin-top: 5px;
  color: #8a6517;
  font-size: 12px;
  font-weight: 650;
  line-height: 18px;
  white-space: nowrap;
}

.daily-renew-card__coin {
  display: inline-grid;
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  place-items: center;
  color: #6b4a06;
  font-size: 10px;
  font-weight: 800;
  background: linear-gradient(145deg, #ffe58a, #d8a62e);
  border: 1px solid rgb(132 91 8 / 22%);
  border-radius: 50%;
  box-shadow:
    inset 0 1px 1px rgb(255 255 255 / 72%),
    0 2px 5px rgb(151 104 15 / 18%);
  animation: daily-renew-coin 2.8s ease-in-out infinite;
}

.daily-renew-placeholder {
  display: grid;
  width: 176px;
  gap: 8px;
  padding: 10px;
  background: #f4f7f2;
  border: 1px solid #e1e8dd;
  border-radius: 12px;
}

.daily-renew-placeholder__line {
  width: 78%;
  height: 10px;
  background: #dfe8dc;
  border-radius: 99px;
}

.daily-renew-placeholder__line--short {
  width: 58%;
}

.daily-renew-error,
.daily-renew-unavailable {
  display: inline-block;
  max-width: 190px;
  overflow: hidden;
  color: #8a5e17;
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.daily-renew-unavailable {
  color: #8b9991;
}

:deep(.el-switch.is-checked .el-switch__core) {
  background-color: #1f7a52;
  border-color: #1f7a52;
}

@keyframes daily-renew-arrive {
  from {
    opacity: 0;
    transform: translateY(5px) scale(.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes daily-renew-coin {
  0%, 72%, 100% { transform: rotateY(0deg); }
  84% { transform: rotateY(180deg); }
}

@media (max-width: 900px) {
  .daily-renew-card {
    width: 116px;
    min-width: 112px;
    max-width: 116px;
    margin-left: auto;
    padding: 8px;
  }

  .daily-renew-card__control {
    gap: 6px;
  }

  .daily-renew-card__label {
    font-size: 12px;
  }

  .daily-renew-card__price {
    gap: 4px;
    font-size: 11px;
    line-height: 14px;
    white-space: normal;
  }

  .daily-renew-card__coin {
    flex-basis: 16px;
    width: 16px;
    height: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .daily-renew-card,
  .daily-renew-card__coin {
    animation: none;
  }
}
</style>
